'use client'

import { Intercom, onShow } from '@intercom/messenger-js-sdk'
import type { InitType } from '@intercom/messenger-js-sdk/dist/types'
import { useEffect } from 'react'
import { useTracking } from '@/components/Tracking'

export interface IntercomAppearance {
  /**
   * When true, the floating launcher bubble is hidden. The messenger can still
   * be opened programmatically via the SDK's show().
   */
  hideDefaultLauncher?: boolean
  alignment?: 'left' | 'right'
  /** Distance in px from the chosen alignment edge to the messenger panel. */
  horizontalPadding?: number
  /** Distance in px from the bottom of the viewport to the messenger panel. */
  verticalPadding?: number
}

export function IntercomProvider({
  userInfo,
  appId,
  appearance,
}: {
  appId: string
  userInfo: Record<string, string> | null
  appearance?: IntercomAppearance
}) {
  const { track } = useTracking()
  const hideDefaultLauncher = appearance?.hideDefaultLauncher
  const alignment = appearance?.alignment
  const horizontalPadding = appearance?.horizontalPadding
  const verticalPadding = appearance?.verticalPadding

  useEffect(() => {
    const intercomOptions: InitType = { app_id: appId }
    if (hideDefaultLauncher !== undefined) {
      intercomOptions.hide_default_launcher = hideDefaultLauncher
    }
    if (alignment) {
      intercomOptions.alignment = alignment
    }
    if (horizontalPadding !== undefined) {
      intercomOptions.horizontal_padding = horizontalPadding
    }
    if (verticalPadding !== undefined) {
      intercomOptions.vertical_padding = verticalPadding
    }
    if (userInfo) {
      Object.assign(intercomOptions, userInfo)
    }

    Intercom(intercomOptions)

    onShow(() => {
      track('button_click', { buttonName: 'intercom' })
    })
  }, [
    userInfo,
    appId,
    track,
    hideDefaultLauncher,
    alignment,
    horizontalPadding,
    verticalPadding,
  ])

  return null
}
